import { connect } from "react-redux";
import * as interviewActions from "../../../actions/interviewActions";
import { first, isEqual } from "lodash-es";
import { FieldArray, Formik, validateYupSchema, yupToFormErrors } from "formik";
import { validationSchema } from "./validationSchema";
import React, { useMemo } from "react";
import * as formsService from "../../../services/formsService";
import OnSubmitValidationError from "../../../components/FormComponents/OnSubmitValidationError";
import InterviewPersonInfoScreeningForm from "./InterviewPersonInfoScreeningForm";
import { getAge } from "../../../services/stringService";
import { WizardButtonsField } from "../Components/WizardButtons";
import { isRequired } from "../../../services/formsService";

const InterviewPersonInfoScreening = ({
    onSubmit,
    onError,
    patient,
    states,
    incomeType,
    incomeFrequency,
    responsibleParty,
    acceptedIncomeProofs,
    householdMembers,
    includeResponsibleParty,
    personInfoScreenings,
    documentTypes,
    navigateToNextPage,
    navigateToPreviousPage,
    maxDependeeAge,
    customerId,
}) => {
    const onFormSubmit = formData => {
        const dataToSubmit = formData.householdMembers.map(x => ({
            personId: x.personId,
            ...x.personInfoScreening,
        }));

        for (const member of dataToSubmit) {
            if (member.income?.unearned != null) {
                for (const incomeType in member.income.unearned) {
                    if (!member.income.unearned.hasOwnProperty(incomeType)) {
                        continue;
                    }

                    const income = member.income.unearned[incomeType];
                    if (income.frequency == null) {
                        income.frequency = "Monthly";
                    }
                }
            }

            if (member.willBeClaimedAsDependent === false) {
                member.claimedAsADependentBy = null;
            }
        }

        if (!isEqual(dataToSubmit, personInfoScreenings)) {
            onSubmit(dataToSubmit);
        }
    };

    function validate(values) {
        const context = values.householdMembers.map(x => ({
            personId: x.personId,
            isPatient: x.personId === patient.personId,
            ...x.demographic,
        }));

        return validateYupSchema(values, validationSchema, false, context)
            .then(() => {
                return {};
            })
            .catch(err => {
                throw yupToFormErrors(err);
            });
    }

    const initValues = useMemo(
        x =>
            extractInitialValues(
                patient,
                includeResponsibleParty,
                responsibleParty,
                [...householdMembers],
                personInfoScreenings
            ),
        [householdMembers, includeResponsibleParty, patient, personInfoScreenings, responsibleParty]
    );

    return (
        <div className="wizard-page">
            <h2 className="title">Financial</h2>
            <Formik
                enableReinitialize={true}
                initialValues={initValues}
                onSubmit={onFormSubmit}
                validate={validate}
                render={props => {
                    const { setFieldValue, setFieldTouched, resetForm, handleSubmit, errors, touched, values } = props;
                    const getValidationState = formsService.getValidationState.bind(this, errors, touched);
                    const getErrors = formsService.getErrors.bind(this, errors);
                    const isRequired = formsService.isRequired.bind(this, validationSchema, values);

                    return (
                        <form className="wizard-form">
                            <OnSubmitValidationError callback={onError} />
                            <FieldArray
                                name="householdMembers"
                                render={() => (
                                    <InterviewPersonInfoScreeningForm
                                        states={states}
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        incomeFrequency={incomeFrequency}
                                        incomeType={incomeType}
                                        acceptedIncomeProofs={acceptedIncomeProofs}
                                        setFieldTouched={setFieldTouched}
                                        allHouseholdMembers={initValues.householdMembers}
                                        getErrors={getErrors}
                                        getValidationState={getValidationState}
                                        documentTypes={documentTypes}
                                        patientPersonId={patient.personId}
                                        responsiblePartyPersonId={responsibleParty.personId}
                                        maxDependeeAge={maxDependeeAge}
                                        isRequired={isRequired}
                                        {...props}
                                    />
                                )}
                            />
                            <WizardButtonsField
                                navigateToNextPage={navigateToNextPage}
                                navigateToPreviousPage={navigateToPreviousPage}
                            />
                        </form>
                    );
                }}
            />
            <div className="required_field_indicator_explained">Indicates required field</div>
        </div>
    );
};

function extractInitialValues(
    patient,
    includeResponsibleParty,
    responsibleParty,
    householdMembers,
    personInfoScreening = []
) {
    const initValues = { householdMembers: [] };

    initValues.householdMembers.push({
        personId: patient.personId,
        demographic: {
            firstName: patient.firstName,
            middleInitial: patient.middleInitial,
            lastName: patient.lastName,
            suffix: patient.suffix,
            dob: patient.dob,
            age: getAge(patient.dob),
            gender: patient.gender,
            relationshipToPatient: "Self",
            maritalStatus: patient.maritalStatus,
        },
    });

    if (includeResponsibleParty) {
        initValues.householdMembers.push({
            personId: responsibleParty.personId,
            demographic: {
                firstName: responsibleParty.firstName,
                middleInitial: responsibleParty.middleInitial,
                lastName: responsibleParty.lastName,
                suffix: responsibleParty.suffix,
                dob: responsibleParty.dob,
                age: getAge(responsibleParty.dob),
                gender: responsibleParty.gender,
                relationshipToPatient: responsibleParty.relationshipToPatient,
                relationshipToResponsibleParty: "Self",
                maritalStatus: responsibleParty.maritalStatus,
                isResponsibleParty: true,
            },
        });
    }

    for (const member of householdMembers) {
        if (!member) {
            continue;
        }
        initValues.householdMembers.push({
            personId: member.personId,
            demographic: {
                firstName: member.firstName,
                middleInitial: member.middleInitial,
                lastName: member.lastName,
                suffix: member.suffix,
                dob: member.dateOfBirth,
                age: getAge(member.dateOfBirth),
                gender: member.gender,
                relationshipToPatient: member.relationshipToPatient,
                relationshipToResponsibleParty: member.relationshipToResponsibleParty,
            },
        });
    }

    for (const member of initValues.householdMembers) {
        const memberPersonInfo = first(personInfoScreening.filter(x => x.personId === member.personId)) || {};

        member.personInfoScreening = {
            personId: member.personId,
            isPregnant: undefined,
            expectedChildren: undefined,
            expectedDueDate: undefined,
            areParentsMarried: undefined,
            isDisabled: undefined,
            willFileJointly: undefined,
            planToFileTaxes: undefined,
            filesJointlyWith: undefined,
            willBeClaimedAsDependent: undefined,
            claimedAsADependentBy: undefined,
            incomeInfo: { doesReceiveIncome: undefined, incomes: [], ...memberPersonInfo.incomeInfo },
            income: {
                employmentStatus: [],
                employers: [{ employerName: "", frequency: "", averageHoursWorkedPerWeek: null, amount: "" }],
            },
            ...memberPersonInfo,
        };

        member.personInfoScreening.spouseInfo =
            member.demographic.relationshipToPatient === "Spouse"
                ? {
                      govId: undefined,
                      issuingState: undefined,
                      socialSecurityNumber: undefined,
                      phone: undefined,
                      phoneType: undefined,
                      hasCoverage: undefined,
                      insuranceNumber: undefined,
                      insuranceName: undefined,
                      ...member.personInfoScreening.spouseInfo,
                  }
                : undefined;
    }
    return initValues;
}

export const InterviewPersonInfoScreeningContainer = connect(
    ({ interview, interviewLookups: lookups, ...state }) => ({
        patient: interview.entry.patient,
        householdMembers: interview.householdMembers,
        responsibleParty: interview.entry.responsibleParty,
        states: lookups.data.states,
        incomeType: lookups.data.incomeTypes,
        incomeFrequency: lookups.data.incomeFrequency,
        acceptedIncomeProofs: lookups.data.acceptedIncomeProofs,
        documentTypes: lookups.data.otherDocumentTypes,
        includeResponsibleParty: interview.entry.isResponsiblePartyDifferentFromPatient,
        personInfoScreenings: interview.personInfoScreening,
        maxDependeeAge: lookups.data.configuration?.maxDependeeAge,
        customerId: state.customer?.data?.customer?.GlobalId,
    }),
    dispatch => ({
        onSubmit: data => dispatch(interviewActions.setPersonsScreeningInfo(data)),
        onError: () => dispatch(interviewActions.setInterviewError("household")),
    })
)(InterviewPersonInfoScreening);
